import { useEffect, useState } from 'react'
import { jsonrpc } from '~/shared/api/jsonrpc'
import { Box, Chart, Header, Stat, StatLabel, StatNumber, dayLabel } from '~/shared/ui'
import { useZone } from '~/team_tools/components/timezone'
import { FigureLine, PeriodFigures } from '~/team_tools/dashboard/statistic/figures'
import { periodKey, usePeriod, useWindowParams } from '~/team_tools/dashboard/statistic/period'

type RailKey = 'card' | 'sol' | 'usdc'

type Figures = {
    usd: number
    count: number
    periods: Record<string, { usd: number; count: number }>
}

type Deposits = {
    rails: Record<RailKey, Figures>
    total: Figures
}

type DepositsHistory = {
    days: number
    /** Date -> [card, sol, usdc] dollars that day, oldest first. */
    history: Record<string, [number, number, number]>
}

type FunnelState = 'completed' | 'awaiting' | 'abandoned' | 'failed' | 'cancelled' | 'reversed'

type DepositsFunnel = Record<RailKey, Record<FunnelState, number>>

// `today`/`week`/`month` are to-date from midnight, Monday and the 1st; the
// `last N` windows are rolling from now. Both are shown because they answer
// different questions, and the labels have to make that difference visible.
const PERIODS: [key: string, label: string][] = [
    ['today', 'Today'],
    ['week', 'This week'],
    ['month', 'This month'],
    ['24_hours', 'Last 24 hours'],
    ['7_days', 'Last 7 days'],
    ['30_days', 'Last 30 days'],
]

const PERIOD_KEYS = PERIODS.map(([key]) => key)

// 31: enough for the longest month, which is the widest window.
const CHART_DAYS = 31

// Order matters and is shared with the backend: the history endpoint returns a
// [card, sol, usdc] triple per day, so this array is what names those columns.
const RAILS: { key: RailKey; label: string; colour: string; note: string }[] = [
    {
        key: 'usdc',
        label: 'Solana USDC',
        colour: 'rgb(34, 197, 94)',
        note: 'Paid in USDC, credited one for one.',
    },
    {
        key: 'sol',
        label: 'Solana SOL',
        colour: '#9945FF',
        note: 'Valued at the SOL price when the deposit landed, never re-priced since.',
    },
    {
        key: 'card',
        label: 'Card (MoonPay)',
        colour: '#eab308',
        note: 'Whatever the buyer paid with; MoonPay delivers USDC.',
    },
]

// Where each rail sits in the history's [card, sol, usdc] triple.
const RAIL_COLUMN: Record<RailKey, number> = { card: 0, sol: 1, usdc: 2 }

// "abandoned" is separated from "awaiting" on purpose: a player who opened the
// deposit screen and walked away is not a payment in flight, and merging the two
// made tens of thousands of the former read as a backlog of the latter.
//
// The labels say what happened to the player's money, not what the record is
// called. This panel is read by people settling a "where is my deposit" ticket
// as often as by engineers, and "awaiting chain" / "never signed" meant nothing
// to them -- both are simply money that never arrived, differing only in how far
// the player got. The keys keep the internal names; only the wording is plain.
const FUNNEL_STATES: [key: FunnelState, label: string, alarming: boolean][] = [
    ['completed', 'completed', false],
    ['awaiting', 'never completed', false],
    ['abandoned', 'left before paying', false],
    ['failed', 'failed', true],
    ['cancelled', 'cancelled', false],
    ['reversed', 'taken back (fraud)', true],
]

const formatUsd = (value: number) =>
    `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const DOLLAR_AXIS = {
    scales: {
        y: {
            min: 0,
            // No precision: 0 -- these are dollars, and a $4.50 day ticking as
            // 4 and 5 reads as a bug.
            ticks: { maxTicksLimit: 5, font: { size: 10 }, callback: (value: number) => `$${value}` },
            grid: { color: 'rgba(255,255,255,0.06)' },
        },
    },
}

/** Every window for one set of figures. */
const PeriodGrid = ({ figures, lines }: { figures?: Figures; lines?: FigureLine[] }) => (
    <PeriodFigures
        chart={lines && { lines, options: DOLLAR_AXIS }}
        // All time on the dashboard; elsewhere the lifetime tile above says it.
        total={
            usePeriod() && figures
                ? { label: 'All time', value: formatUsd(figures.usd), note: `${figures.count} deposits` }
                : undefined
        }
        periods={PERIODS}
        loading={figures === undefined}
        value={(key) => {
            const period = figures?.periods?.[key]
            return period ? formatUsd(period.usd) : undefined
        }}
        note={(key) => {
            const period = figures?.periods?.[key]
            return period ? `${period.count} deposits` : undefined
        }}
    />
)

/** Attempts per rail and how they ended.
 *
 * Read from the deposit tables rather than the ledger, because an attempt that
 * failed never posts a transaction — so this is the only view in which a rail
 * that has started refusing people looks any different from a quiet one. The
 * failed count is coloured only when it is non-zero, so a healthy row stays
 * visually flat and a broken one does not.
 */
const Funnel = ({ funnel }: { funnel?: DepositsFunnel }) => (
    <Box className='w-full gap-2 pt-4'>
        <small className='text-white/50'>
            Every deposit anyone started, all time, and how it ended. An attempt that fails never becomes money, so this
            is the only place it is counted at all.
        </small>
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-2 w-full'>
            {RAILS.map(({ key, label, colour }) => (
                <Stat.Gray key={key} loading={funnel === undefined}>
                    <StatLabel style={{ color: colour }}>{label}</StatLabel>
                    <div className='flex flex-col gap-0.5 pt-1 text-xs'>
                        {FUNNEL_STATES.map(([state, stateLabel, alarming]) => {
                            const count = funnel?.[key]?.[state]
                            return (
                                <div key={state} className='flex flex-row justify-between gap-2'>
                                    <span className='text-white/40'>{stateLabel}</span>
                                    <span className={alarming && !!count ? 'text-rose-400' : 'text-white/80'}>
                                        {count ?? '—'}
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                </Stat.Gray>
            ))}
        </div>
        {/* Spelled out because the two wordings people trip over are the two that
            mean "no money arrived", and the difference between them is how far the
            player got — which is what decides whether a support ticket is a
            payment to chase or a player who simply changed their mind. */}
        <small className='text-white/40'>
            “Never completed” means the money never arrived and nobody was credited — though a deposit started in the
            last few minutes may still be on its way. “Left before paying” is someone who opened the deposit screen and
            closed it without ever paying, so there was never any money to lose.
        </small>

        {/* The one place the two halves of this section have to be reconciled by
            hand. The money above is gross, so a reversed deposit still counts on
            the day it landed; the funnel reads current status, and the fraud tool
            leaves a reversed deposit marked ERROR. So the deposit counts on the
            rail tiles are completed + reversed, not completed alone. Saying so
            here is cheaper than making one of the two lie. */}
        <small className='text-white/40'>
            A deposit taken back for fraud is counted under both “failed” and “taken back”, and the money above still
            includes it — so a rail’s deposit count is its completed plus its taken back.
        </small>
    </Box>
)

export const BankDeposits = () => {
    const [deposits, setDeposits] = useState<Deposits>()
    const [funnel, setFunnel] = useState<DepositsFunnel>()
    const [labels, setLabels] = useState<string[]>([])
    const [series, setSeries] = useState<Record<RailKey, number[]>>({ card: [], sol: [], usdc: [] })
    const [history, setHistory] = useState<DepositsHistory['history']>({})
    const period = usePeriod()
    const window = useWindowParams()

    // The windows and chart days are days in the zone the shell names.
    const [zone] = useZone()

    const getData = () => {
        let live = true
        jsonrpc('statistic.deposits', {
            periods: PERIOD_KEYS.concat(window.custom ? ['custom'] : []),
            tz: zone,
            ...window.figure,
        }).then((res) => live && setDeposits(res.result))
        jsonrpc('statistic.deposits.funnel').then((res) => live && setFunnel(res.result))
        setHistory({})
        jsonrpc(
            'statistic.deposits.history',
            period ? { ...window.history, tz: zone } : { days: CHART_DAYS, tz: zone }
        ).then((res) => {
            if (!live) return
            const history: DepositsHistory['history'] = res.result?.history || {}
            setHistory(history)
            const rows = Object.values(history)
            setLabels(Object.keys(history).map(dayLabel))
            setSeries({
                card: rows.map(([card]) => card),
                sol: rows.map(([, sol]) => sol),
                usdc: rows.map(([, , usdc]) => usdc),
            })
        })
        return () => {
            live = false
        }
    }

    useEffect(() => getData(), [zone, window.key])

    return (
        <Box className='w-full text-center gap-2'>
            <Header>Deposits</Header>
            <small className='text-white/50'>
                Money players put in, by the rail they used. Figures are gross — a deposit later reversed for fraud
                still counts on the day it landed, and fraud-tagged accounts' deposits are included: this is money that
                arrived, whoever sent it.
            </small>

            <Box className='w-full gap-2 pt-2'>
                {!period && (
                    <Stat.Gray className='w-full' loading={deposits === undefined}>
                        <StatLabel className='text-amber-500'>All rails — all time</StatLabel>
                        <StatNumber>{deposits ? formatUsd(deposits.total.usd) : undefined}</StatNumber>
                        <StatLabel className='text-white/40'>
                            {deposits ? `${deposits.total.count} deposits` : undefined}
                        </StatLabel>
                    </Stat.Gray>
                )}
                <PeriodGrid
                    figures={deposits?.total}
                    lines={RAILS.map(({ key, label, colour }) => ({
                        label,
                        colour,
                        history: Object.fromEntries(
                            Object.entries(history).map(([day, row]) => [day, row[RAIL_COLUMN[key]]])
                        ),
                    }))}
                />
            </Box>

            {/* Each rail's figure, coloured to match its line on the chart --
                the legend alone will not tell you which of three similar lines
                is card money. On the dashboard they split the window above;
                anywhere else they are lifetime totals and say so. */}
            <Box className='w-full gap-2 pt-4'>
                <small className='text-white/50'>
                    {period
                        ? 'By rail, over the same window.'
                        : 'Lifetime totals per rail — these sum to the all-time figure above, not to any window.'}
                </small>
                <div className='grid grid-cols-1 sm:grid-cols-3 gap-2 w-full'>
                    {RAILS.map(({ key, label, colour, note }) => {
                        const rail = deposits?.rails[key]
                        const figure = rail && period && period !== 'all' ? rail.periods[periodKey(period)] : rail
                        return (
                            <Stat.Gray key={key} loading={figure === undefined} title={note}>
                                <StatLabel style={{ color: colour }}>
                                    {label}
                                    {period ? '' : ' — all time'}
                                </StatLabel>
                                <StatNumber>{figure ? formatUsd(figure.usd) : undefined}</StatNumber>
                                <StatLabel className='text-white/40'>
                                    {figure ? `${figure.count} deposits` : undefined}
                                </StatLabel>
                            </Stat.Gray>
                        )
                    })}
                </div>
            </Box>

            {/* On the dashboard the chart is drawn under the headline, over the
                chosen window; this fixed one is for callers with no window. */}
            {!period && (
                <Box className='w-full gap-2 pt-4'>
                    <small className='text-white/50'>Dollars deposited per day, last {CHART_DAYS} days</small>
                    <Box.Gray className='w-full'>
                        <Chart.TimeSeries
                            className='h-48 sm:h-56'
                            data={{
                                labels,
                                datasets: RAILS.map(({ key, label, colour }) => ({
                                    label,
                                    data: series[key].map(Number),
                                    borderColor: colour,
                                    backgroundColor: colour,
                                })),
                            }}
                            options={{
                                scales: {
                                    y: {
                                        min: 0,
                                        // No precision: 0 — these are dollars, and a
                                        // $4.50 day ticking as 4 and 5 reads as a bug.
                                        ticks: {
                                            maxTicksLimit: 5,
                                            font: { size: 10 },
                                            callback: (value: number) => `$${value}`,
                                        },
                                        grid: { color: 'rgba(255,255,255,0.06)' },
                                    },
                                },
                            }}
                        />
                    </Box.Gray>
                </Box>
            )}

            <Funnel funnel={funnel} />
        </Box>
    )
}
